import * as React from 'react';
import snakeCase from 'lodash/snakeCase';

import { __t, applyTranslations } from 'utils/language';
import { getCountMetric, getRateMetric } from 'utils/metrics';
import { getDisplayDate } from 'utils/date';
import {
  DELIVERABLE_TYPE_INSTAGRAM_POST as IG_POST_TYPE,
  DELIVERABLE_TYPE_INSTAGRAM_REEL as IG_REEL_TYPE,
  DELIVERABLE_TYPE_INSTAGRAM_STORY as IG_STORY_TYPE,
  DELIVERABLE_TYPE_TIKTOK_VIDEO as TT_VIDEO_TYPE,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO as YT_VIDEO_TYPE
} from 'utils/deliverables';
import { renderReactTooltipToElement } from 'components/graphs/echarts/util/echarts-react-tooltip';

import type { DOMRect } from 'types/Dom.type';
import type {
  BarGraphMultiFormatterParam,
  BarGraphMultiFormatterParamSize,
  BarGraphMultiTooltip
} from '../types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('../shared/bar-graph-multi-shared.i18n.json'));

const COMMON_MSG_KEY = 'collabary_common';
const SHARED_MSG_KEY = 'bar_graph_multi_shared';

const DATA_TYPES = [
  IG_POST_TYPE,
  IG_STORY_TYPE,
  IG_REEL_TYPE,
  TT_VIDEO_TYPE,
  YT_VIDEO_TYPE
];

export const getRow = (
  key: string,
  value: number,
  isEngagementRate: boolean
): React.ReactNode => (
  value <= 0 ? '' : (
    <div className="bar-graph-multi__tooltip-row" key={key}>
      <div className="bar-graph-multi__tooltip-label">
        {__t(`${SHARED_MSG_KEY}.labels.${key}`)}
      </div>
      <div className="bar-graph-multi__tooltip-value">
        {isEngagementRate ? getRateMetric(value) : getCountMetric(value)}
      </div>
    </div>
  )
);

export const getFormattedTooltip = (
  item: BarGraphMultiFormatterParam
): React.ReactElement => {
  const isEngagementRate = item.data?.kpi ? item.data.kpi.includes('engagement') : false;
  let total = 0;
  const rows: Array<React.ReactNode> = [];
  let date = '';

  Object.keys(item.data).forEach(key => {
    if (DATA_TYPES.includes(key)) {
      const value = item.data[key];
      total += value;
      date = item.data.date;
      rows.push(getRow(key, value, isEngagementRate));
    }
  });

  const formattedTotal = isEngagementRate
    ? getRateMetric(total)
    : getCountMetric(total);

  return (
    <div className="bar-graph-multi__tooltip">
      <div className="bar-graph-multi__tooltip-row">
        <div className="bar-graph-multi__tooltip-total">
          Total {__t(`${COMMON_MSG_KEY}.kpis.${snakeCase(item.data.kpi)}`).toLowerCase()}:
        </div>
        <div className="bar-graph-multi__tooltip-total bar-graph-multi__tooltip-value">
          {formattedTotal}
        </div>
      </div>
      {rows}
      <div className="bar-graph-multi__tooltip-row">
        <div className="bar-graph-multi__tooltip-date">
          {getDisplayDate(date)}
        </div>
      </div>
    </div>
  );
};

export const getTooltip = (): BarGraphMultiTooltip => ({
  trigger: 'item',
  enterable: true,
  transitionDuration: 0.4,
  padding: [16, 16, 12, 16],
  backgroundColor: '#fff',
  extraCssText: 'box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.14); border-color: #e0e0e0;',
  textStyle: {
    fontFamily: 'Hind',
    color: '#3a3a3a'
  },
  position: (
    mousePosition: Array<number>,
    param: BarGraphMultiFormatterParam | undefined | null,
    dom: HTMLElement | undefined | null,
    rect: DOMRect | undefined | null,
    size: BarGraphMultiFormatterParamSize
  ): Array<number> => {
    const x = mousePosition[0] - (size.contentSize[0] / 2);
    const y = (mousePosition[1] - size.contentSize[1]) - 12;

    return [x, y];
  },
  formatter: (param: BarGraphMultiFormatterParam): HTMLElement => (
    renderReactTooltipToElement('bar-graph-multi', getFormattedTooltip(param))
  )
});
